import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ZiraService, environment } from '@dal/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
@Component({
  selector: 'dal-shared-ui-media-renderer',
  templateUrl: './media-renderer.component.html',
  styleUrl: './media-renderer.component.scss',
})
export class MediaRendererComponent implements OnChanges, OnDestroy {
  @Input() url!: string;
  @Input() type!: string;
  @Input() imagePreview!: boolean;
  @Input() applyClass: boolean = true;
  @Input() authenticateUrl: boolean = true;
  @Input() customClass: string = '';

  @ViewChild('audioPlayer', { static: false })
  audioPlayer!: ElementRef<HTMLAudioElement>;
  @ViewChild('videoPlayer', { static: false })
  videoPlayer!: ElementRef<HTMLVideoElement>;

  renditionId: string;
  originalId: string;
  inputUrl: string | null = null; // raw string
  safeUrl: SafeResourceUrl | null = null; // sanitized value
  idToFetch: string;

  private currentBlobUrl: string | null = null;
  private readonly destroy$ = new Subject<void>();

  // Update trusted domains based on workspace context
  private trustedDomains: string[] = [new URL(environment.apiBaseUrl).hostname, 'localhost'];

  constructor(
    private ziraService: ZiraService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['url'] && this.url) {
      if (this.authenticateUrl) {
        this.extractIdsFromUrl(this.url);
        this.idToFetch = this.renditionId || this.originalId;
        if (this.idToFetch) {
          this.ziraService.getAssetUrl(this.idToFetch).pipe(takeUntil(this.destroy$)).subscribe((blob) => {
            // Revoke the previous blob URL before creating a new one to prevent memory leaks.
            if (this.currentBlobUrl) {
              URL.revokeObjectURL(this.currentBlobUrl);
            }
            const objectUrl = URL.createObjectURL(blob);
            this.currentBlobUrl = objectUrl;
            this.inputUrl = objectUrl;
            this.safeUrl = this.sanitizeAndValidateUrl(objectUrl);
            // Force CD to flush safeUrl into the DOM binding before calling load().
            // Without this, the <source src> / <audio src> attribute is still stale
            // when load() is called and the browser loads nothing.
            this.cdr.detectChanges();
            this.videoPlayer?.nativeElement.load();
            this.audioPlayer?.nativeElement.load();
          });
        }
      } else {
        this.inputUrl = this.url;
        this.safeUrl = this.sanitizeAndValidateUrl(this.url);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
    }
  }

  private extractIdsFromUrl(url: string): void {
    const parts = url.split('/');
    if (parts.length >= 2) {
      this.renditionId = parts[parts.length - 1];
      this.originalId = parts[parts.length - 2];
    } else if (parts.length === 1) {
      this.originalId = parts[0];
    }
  }


  private sanitizeAndValidateUrl(url: string | null): SafeResourceUrl | null {
    if (!url) return null;
    try {
      const parsedUrl = new URL(url, window.location.origin);
      // Allow only same-origin blob URLs
      if (parsedUrl.protocol === 'blob:') {
        const blobPrefix = `blob:${window.location.origin}/`;
        if (url.startsWith(blobPrefix)) {
          return this.sanitizer.bypassSecurityTrustResourceUrl(url);
        }
        console.warn('Untrusted blob URL:', url);
        return null;
      }
      // Allow only same-origin local static asset URLs
      if (parsedUrl.pathname.startsWith('/assets/') && parsedUrl.origin === window.location.origin) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(parsedUrl.toString());
      }
      // Only allow http(s) URLs from trusted domains
      if (['http:', 'https:'].includes(parsedUrl.protocol) && this.trustedDomains.includes(parsedUrl.hostname)) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(parsedUrl.toString());
      }
    } catch (e) {
      console.error('Invalid URL:', url);
    }
    // Log or handle invalid or untrusted URLs
    console.warn('Untrusted or invalid URL:', url);
    return null;
  }
  
  get imageUrl(): string {
    if (!this.inputUrl) return '';
    // Allow blob URLs directly
    if (this.inputUrl.startsWith('blob:')) {
      return this.inputUrl;
    }
    // Allow local static asset URLs
    if (this.inputUrl.startsWith('/assets/')) {
      return this.inputUrl;
    }
    // Only allow http(s) URLs from trusted domains
    const urlPattern = /^https?:\/\//;
    if (urlPattern.test(this.inputUrl)) {
      try {
        const parsedUrl = new URL(this.inputUrl);
        if (this.trustedDomains.includes(parsedUrl.hostname)) {
          return this.inputUrl;
        }
      } catch (e) {
        console.error('Invalid image URL:', this.inputUrl);
      }
    }
    console.warn('Untrusted or invalid image URL:', this.inputUrl);
    return '';
  }
}
